import { useEffect, useMemo, useState } from 'react';
import type { TUseManagerAssigneeFilterMenuItemsParams } from '~/modules/covenants/CovenantsDataForm/CovenantsFormByManager/types.ts';

const normalizeAssigneeValue = (value: string) => value.trim().toLowerCase();

export const useManagerAssigneeFilterMenuItems = ({
  assigneeOptions,
  selectedAssignee,
  closeMenu,
  onSelectAssignee,
}: TUseManagerAssigneeFilterMenuItemsParams) => {
  const [query, setQuery] = useState('');
  const [draftSelectedAssignees, setDraftSelectedAssignees] = useState<string[]>(selectedAssignee);
  const trimmedQuery = query.trim();

  useEffect(() => {
    setDraftSelectedAssignees(selectedAssignee);
  }, [selectedAssignee]);

  const resolvedQueryAssignee = useMemo(() => {
    if (!trimmedQuery) {
      return '';
    }

    return (
      assigneeOptions.find(
        (assignee) => normalizeAssigneeValue(assignee) === normalizeAssigneeValue(trimmedQuery)
      ) ?? trimmedQuery
    );
  }, [assigneeOptions, trimmedQuery]);

  const hasResolvedQueryInSelection = useMemo(
    () =>
      !!resolvedQueryAssignee &&
      draftSelectedAssignees.some(
        (assignee) =>
          normalizeAssigneeValue(assignee) === normalizeAssigneeValue(resolvedQueryAssignee)
      ),
    [draftSelectedAssignees, resolvedQueryAssignee]
  );

  const handleAddAssignee = (assignee: string) => {
    setDraftSelectedAssignees((previousState) =>
      previousState.some(
        (currentAssignee) =>
          normalizeAssigneeValue(currentAssignee) === normalizeAssigneeValue(assignee)
      )
        ? previousState
        : [...previousState, assignee]
    );
    setQuery('');
  };

  const handleAddQueryAssignee = () => {
    if (!resolvedQueryAssignee || hasResolvedQueryInSelection) {
      return;
    }

    handleAddAssignee(resolvedQueryAssignee);
  };

  const handleRemoveAssignee = (assignee: string) => {
    setDraftSelectedAssignees((previousState) =>
      previousState.filter(
        (currentAssignee) =>
          normalizeAssigneeValue(currentAssignee) !== normalizeAssigneeValue(assignee)
      )
    );
  };

  const handleApply = () => {
    const nextSelectedAssignees =
      resolvedQueryAssignee && !hasResolvedQueryInSelection
        ? [...draftSelectedAssignees, resolvedQueryAssignee]
        : draftSelectedAssignees;

    onSelectAssignee(nextSelectedAssignees);
    closeMenu();
  };

  const handleReset = () => {
    setDraftSelectedAssignees([]);
    setQuery('');
  };

  const handleQueryChange = (value: string) => {
    setQuery(value);
  };

  const handleQueryKeyDown = (key: string) => {
    if (key !== 'Enter') {
      return false;
    }

    handleAddQueryAssignee();
    return true;
  };

  return {
    query,
    draftSelectedAssignees,
    handleApply,
    handleQueryChange,
    handleQueryKeyDown,
    handleRemoveAssignee,
    handleReset,
  };
};

export default useManagerAssigneeFilterMenuItems;
